import type { Vec2 } from '../../geo/types';
import type { SiteModel } from '../../site/types';
import type { Obstacle } from '../types';
import { distancePointSegment, pointInRing, ringBounds } from '../../geo/polygon';
import { createSpatialHash } from '../../geo/spatialHash';

/** A lighter's turning envelope keeps its hull clear even while backing away from a berth. */
export function lighterWater(site: SiteModel, barriers: readonly Obstacle[]) {
  const hash = createSpatialHash<Obstacle>(32);
  for (const barrier of barriers) hash.insert(barrier, ringBounds(barrier.polygon));
  const open = ([x, y]: Vec2): boolean => {
    const nearby = hash.query({ minX: x - 4.4, minY: y - 4.4, maxX: x + 4.4, maxY: y + 4.4 });
    if (
      nearby.some(
        (b) =>
          pointInRing([x, y], b.polygon) ||
          b.polygon.some(
            (a, i) =>
              distancePointSegment([x, y], { a, b: b.polygon[(i + 1) % b.polygon.length]! }) < 4.4
          )
      )
    )
      return false;
    for (let i = 0; i <= 16; i++) {
      const angle = (i * Math.PI) / 8;
      const radius = i === 16 ? 0 : 4.4;
      const p: Vec2 = [x + Math.cos(angle) * radius, y + Math.sin(angle) * radius];
      if (!site.waterIndex.contains(p) || nearby.some((b) => pointInRing(p, b.polygon)))
        return false;
    }
    return true;
  };
  const clear = (a: Vec2, b: Vec2): boolean => {
    const steps = Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]));
    for (let i = 0; i <= steps; i++) {
      const t = steps ? i / steps : 0;
      if (!open([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])) return false;
    }
    return true;
  };
  return { open, clear };
}

/** The real water and standing fleet define the route; no boat crosses a closed pile row. */
export function lighterPath(
  from: Vec2,
  to: Vec2,
  water: ReturnType<typeof lighterWater>
): Vec2[] | null {
  if (!water.open(from) || !water.open(to)) return null;
  const step = 6,
    reach = 110;
  const x0 = Math.floor((Math.min(from[0], to[0]) - reach) / step) * step;
  const y0 = Math.floor((Math.min(from[1], to[1]) - reach) / step) * step;
  const nx = Math.ceil((Math.max(from[0], to[0]) + reach - x0) / step) + 1;
  const ny = Math.ceil((Math.max(from[1], to[1]) + reach - y0) / step) + 1;
  const point = (id: number): Vec2 => [x0 + (id % nx) * step, y0 + Math.floor(id / nx) * step];
  const cached = new Int8Array(nx * ny);
  const isOpen = (id: number) => {
    if (!cached[id]) cached[id] = water.open(point(id)) ? 1 : -1;
    return cached[id] === 1;
  };
  const attach = (p: Vec2): number => {
    const ix = Math.round((p[0] - x0) / step),
      iy = Math.round((p[1] - y0) / step);
    const candidates: number[] = [];
    for (let dy = -2; dy <= 2; dy++)
      for (let dx = -2; dx <= 2; dx++) {
        const x = ix + dx,
          y = iy + dy;
        if (x >= 0 && y >= 0 && x < nx && y < ny) candidates.push(y * nx + x);
      }
    candidates.sort(
      (a, b) =>
        Math.hypot(point(a)[0] - p[0], point(a)[1] - p[1]) -
        Math.hypot(point(b)[0] - p[0], point(b)[1] - p[1])
    );
    return candidates.find((id) => isOpen(id) && water.clear(p, point(id))) ?? -1;
  };
  const start = attach(from),
    end = attach(to);
  if (start < 0 || end < 0) return null;
  const parent = new Int32Array(nx * ny).fill(-1),
    queue = new Int32Array(nx * ny);
  let first = 0,
    count = 1;
  queue[0] = start;
  parent[start] = start;
  while (first < count && parent[end] === -1) {
    const id = queue[first++]!,
      x = id % nx,
      y = Math.floor(id / nx);
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const xx = x + dx,
        yy = y + dy;
      if (xx < 0 || yy < 0 || xx >= nx || yy >= ny) continue;
      const next = yy * nx + xx;
      if (parent[next] !== -1 || !isOpen(next) || !water.clear(point(id), point(next))) continue;
      parent[next] = id;
      queue[count++] = next;
    }
  }
  if (parent[end] === -1) return null;
  const path: Vec2[] = [to];
  for (let id = end; id !== start; id = parent[id]!) path.push(point(id));
  path.push(point(start), from);
  path.reverse();
  const simplified: Vec2[] = [path[0]!];
  for (let i = 0; i < path.length - 1;) {
    let j = path.length - 1;
    while (j > i + 1 && !water.clear(path[i]!, path[j]!)) j--;
    simplified.push(path[j]!);
    i = j;
  }
  return simplified;
}
